import type { PropDef, PropKind, TrackDef, Vec2 } from '../core/types';
import { hash01 } from '../core/math';
import { roundedLoop } from './track-geometry';

/**
 * „Balatoni nyár" – tóparti kör.
 * Folyamatos, nagy ívű kanyarok, kevés épület, sok fa és pad a vízparton.
 */
const CENTERLINE: Vec2[] = roundedLoop([
  { x: 600, y: 1800, r: 520 },
  { x: 2600, y: 1800, r: 520 },
  { x: 2600, y: 600, r: 420 },
  { x: 600, y: 600, r: 420 },
]);

const props: PropDef[] = [];
const add = (kind: PropKind, s: number, lateral: number, scale = 1, variant = 0): void => {
  props.push({ kind, s, lateral, scale, variant });
};

// belső ív: fás park, padok, büfék
for (let i = 0; i < 64; i++) {
  const s = i / 64;
  const h = hash01(i * 92821 + 13);
  const h2 = hash01(i * 31337 + 31);
  if (h > 0.12) add('tree', s, -(186 + h2 * 58), 0.8 + h * 0.6, Math.floor(h2 * 3));
  if (h2 > 0.62) add('bench', s + 0.005, -160, 0.9 + h * 0.3, Math.floor(h2 * 2));
  if (h < 0.1) add('kiosk', s + 0.004, -176, 0.9, 0);
}

// külső oldal: víz, ezért csak a belső partra kerülnek fák, padok és büfék
// lámpák a belső járdán
for (let i = 0; i < 30; i++) add('lamp', i / 30, -188, 1, i % 3);

for (let i = 0; i < 18; i++) {
  const s = (i + 0.3) / 18;
  const h = hash01(i * 5501 + 11);
  if (h < 0.4) add('bin', s, -172, 1, 0);
  else if (h < 0.7) add('sign', s, -184, 1, i % 4);
}

for (const s of [0.12, 0.36, 0.62, 0.88]) add('crosswalk', s, 0, 1, 0);
for (const s of [0.25, 0.57, 0.87]) {
  add('arrow', s, -48, 1, 0);
  add('arrow', s + 0.012, 48, 1, 1);
}

add('puddle', 0.24, 46, 1.3, 0);
add('parkedCar', 0.68, -204, 1, 2);
add('parkedCar', 0.695, -204, 1, 0);

export const LAKE_TRACK: TrackDef = {
  id: 'balatoni-nyar',
  name: 'Balatoni nyár',
  subtitle: 'Tóparti kör · 3 kör',
  description:
    'Nyári tópart: nagy, folyamatos ívek, fák és padok a belső oldalon. Kevesebb a fal, de a víz felőli kanyarokban könnyű kiszélesíteni.',
  centerline: CENTERLINE,
  roadHalfWidth: 100,
  kerbWidth: 12,
  sidewalkWidth: 32,
  startS: 0.03,
  laps: 3,
  theme: 'lake',
  props,
  boostPads: [
    { s: 0.16, lateral: 0, length: 240, width: 150 },
    { s: 0.44, lateral: 0, length: 240, width: 150 },
    { s: 0.7, lateral: 0, length: 240, width: 150 },
  ],
  itemRows: [
    { s: 0.1, lateral: 0, count: 4, spacing: 54 },
    { s: 0.3, lateral: 0, count: 4, spacing: 54 },
    { s: 0.58, lateral: 0, count: 4, spacing: 54 },
    { s: 0.82, lateral: 0, count: 4, spacing: 54 },
  ],
  ambient: {
    sky: '#1d3a47',
    asphalt: '#4a4f58',
    asphaltDark: '#3c4149',
    lane: '#f0e2b0',
    kerb: '#c95a45',
    sidewalk: '#cbbd9a',
    wall: '#2f6470',
    accent: '#7ed0c0',
    shadow: 'rgba(8,14,18,0.4)',
  },
  cameraZoom: 0.95,
};
